import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Heart, ArrowRight } from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
} from "@b2b/ui-components";
import { favoritesService } from "@b2b/services";
import type { FavoriteProduct } from "@b2b/services";
import { getProductDetailPath } from "../../../lib/productRoutes";

function formatPrice(price: number, currency?: string) {
  const code = currency?.trim() || "INR";
  try {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: code,
      maximumFractionDigits: 0,
    }).format(price);
  } catch {
    return `₹${Number(price).toLocaleString("en-IN")}`;
  }
}

export function RecentFavorites() {
  const [favorites, setFavorites] = useState<FavoriteProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchFavorites = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await favoritesService.getFavorites();
        const list = res?.favorites ?? [];
        setFavorites(list.slice(0, 4));
      } catch (err: unknown) {
        console.error("Error fetching recent favorites:", err);
        setError(err instanceof Error ? err.message : "Failed to load");
        setFavorites([]);
      } finally {
        setLoading(false);
      }
    };
    fetchFavorites();
  }, []);

  return (
    <Card className="shadow-md h-full">
      <CardHeader className="pb-4 flex flex-row items-center justify-between gap-2">
        <CardTitle className="text-lg font-semibold flex items-center gap-2">
          <Heart className="w-5 h-5 text-orange-600" />
          Saved Products
        </CardTitle>
        <Button variant="ghost" size="sm" asChild className="shrink-0">
          <Link to="/buyer/favorites" className="flex items-center gap-1">
            View all
            <ArrowRight className="w-4 h-4" />
          </Link>
        </Button>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-16 bg-gray-100 rounded animate-pulse" />
            ))}
          </div>
        ) : error ? (
          <div className="p-4 rounded-lg border border-amber-200 bg-amber-50 text-amber-800 text-sm">
            {error}
          </div>
        ) : favorites.length === 0 ? (
          <div className="p-6 rounded-lg border border-dashed border-gray-200 text-center text-gray-500 text-sm">
            No saved products yet. Tap the heart on any product to save it here.
          </div>
        ) : (
          <ul className="space-y-3">
            {favorites.map((product) => {
              const image =
                product.thumbnailImage ||
                product.images?.[0] ||
                "/placeholder-product.png";
              return (
                <li key={product.id}>
                  <Link
                    to={getProductDetailPath(product)}
                    className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition"
                  >
                    <img
                      src={image}
                      alt={product.name}
                      className="w-12 h-12 rounded object-cover bg-gray-100 shrink-0"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src =
                          "/placeholder-product.png";
                      }}
                    />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 line-clamp-1">
                        {product.name}
                      </p>
                      <p className="text-xs text-gray-500 line-clamp-1">
                        {product.seller?.name
                          ? `${product.seller.name} · `
                          : ""}
                        {formatPrice(product.price, product.currency)}
                      </p>
                    </div>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
